import DOMPurify from 'dompurify';
import { RICH_TEXT_HTML_ALLOWLIST } from '@hygge/shared';

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Allows safe HTML tags and removes dangerous scripts. The allowlist lives in
 * `@hygge/shared` (RICH_TEXT_HTML_ALLOWLIST) so this render-time sanitizer and
 * the backend save-time sanitizer cannot drift (HW-704).
 */
export function sanitizeHTML(html: string): string {
  return DOMPurify.sanitize(html, RICH_TEXT_HTML_ALLOWLIST);
}

/**
 * Same gate, plus inline images — for rendering markdown that came from the
 * Jira importer, where a comment's screenshot is often the whole point of
 * having imported the comment.
 *
 * Safe to widen here because the markdown is parsed with `html: false`: the
 * only `<img>` that can reach this function is one markdown's own image syntax
 * produced, whose URL markdown-it already ran through `validateLink`
 * (javascript:, vbscript: and hostile data: URIs are rejected there). DOMPurify
 * still strips every event-handler attribute regardless of the tag allowlist,
 * so `onerror` cannot survive. `src` is additionally held to our own upload
 * path or plain https, so a comment cannot beacon out to an arbitrary host.
 *
 * The shared allowlist is deliberately NOT changed: it is also the backend's
 * save-time gate for recruitment content, which has no reason to carry images.
 */
export function sanitizeRichTextWithImages(html: string): string {
  const cleaned = DOMPurify.sanitize(html, {
    ...RICH_TEXT_HTML_ALLOWLIST,
    ALLOWED_TAGS: [...RICH_TEXT_HTML_ALLOWLIST.ALLOWED_TAGS, 'img'],
    ALLOWED_ATTR: [...RICH_TEXT_HTML_ALLOWLIST.ALLOWED_ATTR, 'src', 'alt'],
  });

  // Second pass: keep only images we serve ourselves or plain https.
  const doc = new DOMParser().parseFromString(`<div>${cleaned}</div>`, 'text/html');
  for (const img of Array.from(doc.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    if (!src.startsWith('/uploads/') && !src.startsWith('https://')) img.remove();
  }
  return doc.body.firstElementChild?.innerHTML ?? '';
}
